import { ACTION_COUNT } from './env/constants';

/** Pick the legal action with the highest logit (deterministic). */
export function argmaxMasked(
  logits: Float32Array | number[],
  mask: Float32Array
): number {
  let best = -1;
  let bestScore = -Infinity;
  for (let i = 0; i < ACTION_COUNT; i += 1) {
    if (mask[i] <= 0) continue;
    const v = Number(logits[i]);
    if (v > bestScore) {
      bestScore = v;
      best = i;
    }
  }
  if (best < 0) throw new Error('No legal actions to sample');
  return best;
}

/**
 * Temperature softmax + nucleus (top-p) sampling over masked logits.
 */
export function sampleMaskedTopP(
  logits: Float32Array | number[],
  mask: Float32Array,
  temperature: number,
  topP: number
): number {
  const legal: number[] = [];
  const scores: number[] = [];
  let maxLogit = -Infinity;
  const temp = Math.max(temperature, 1e-3);
  for (let i = 0; i < ACTION_COUNT; i += 1) {
    if (mask[i] <= 0) continue;
    const v = Number(logits[i]) / temp;
    legal.push(i);
    scores.push(v);
    if (v > maxLogit) maxLogit = v;
  }
  if (legal.length === 0) throw new Error('No legal actions to sample');

  let sum = 0;
  const probs = scores.map((w) => {
    const p = Math.exp(w - maxLogit);
    sum += p;
    return p;
  });
  for (let i = 0; i < probs.length; i += 1) probs[i] /= sum;

  const order = probs
    .map((p, i) => ({ i, p }))
    .sort((a, b) => b.p - a.p);

  const cutoff = Math.min(Math.max(topP, 1e-6), 1);
  const kept: { i: number; p: number }[] = [];
  let cum = 0;
  for (const row of order) {
    kept.push(row);
    cum += row.p;
    if (cum >= cutoff) break;
  }

  let mass = 0;
  for (const row of kept) mass += row.p;
  let r = Math.random() * mass;
  for (const row of kept) {
    r -= row.p;
    if (r <= 0) return legal[row.i];
  }
  return legal[kept[kept.length - 1].i];
}
